import {
    createContext,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { DisplaySizes } from '../components/cms/CmsPagePreview';
import { useSelector } from 'react-redux';
import { SettingsState, Store } from '../store/types';

export type PreviewContextType = {
    previewUrl?: string;
    showPreview: boolean;
    inNewTab: boolean;
    previewSize: DisplaySizes;
    buildPreviewUrl: (locale?: string) => string;
    updatePreview: (viewProps?: any) => void;
    setWindow: (window: Window | null | undefined) => void;
    setPreviewSize: (newSize: DisplaySizes) => void;
    openInNewTab: (locale?: string) => void;
    setPreviewToken: (previewToken: string | false) => void;
};

type PreviewContextProviderProps = {
    children: ReactElement;
};

const defaultContext: PreviewContextType = {
    previewUrl: undefined,
    showPreview: false,
    inNewTab: false,
    previewSize: 'default',
    buildPreviewUrl: () => '',
    updatePreview: () => {},
    setWindow: () => {},
    setPreviewSize: () => {},
    openInNewTab: () => {},
    setPreviewToken: () => {},
};

const PreviewContext = createContext<PreviewContextType>(defaultContext);

export default function PreviewContextProvider({
    children,
}: PreviewContextProviderProps) {
    const windowRef = useRef<Window>();
    const { i18n, branding } = useSelector<Store, SettingsState>(
        state => state.settings,
    );

    const [previewUrl, setPreviewUrl] = useState<string>();
    const [showPreview, setShowPreview] = useState(false);
    const [inNewTab, setInNewTab] = useState(false);
    const [previewSize, setPreviewSize] = useState<DisplaySizes>('default');
    const [previewProps, setPreviewProps] = useState({});

    const updatePreviewWindow = useCallback(() => {
        if (showPreview) {
            windowRef.current?.postMessage({ previewProps }, '*');
        }
    }, [previewProps, showPreview]);

    useEffect(() => {
        if (previewProps && Object.keys(previewProps).length > 0) {
            setShowPreview(true);
        }
    }, [previewProps]);

    useEffect(updatePreviewWindow, [updatePreviewWindow, previewProps]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        const listenPageEvents = (event: MessageEvent) => {
            if (event.data === 'preview:opened') {
                timeout = setTimeout(updatePreviewWindow, 250);
            }
            if (event.data === 'preview:closed') setInNewTab(false);
        };
        window.addEventListener('message', listenPageEvents);
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
            window.removeEventListener('message', listenPageEvents);
        };
    }, [updatePreviewWindow]);

    const updatePreview = (newViewProps?: any) => {
        if (typeof newViewProps !== 'undefined') {
            setPreviewProps(newViewProps);
        } else {
            updatePreviewWindow();
        }
    };

    const setWindow = (window: Window | null | undefined) => {
        if (window) {
            windowRef.current = window;
        }
    };

    const buildPreviewUrl = (locale: string = i18n) => {
        if (branding?.frontendInfoEndpoint) {
            const url = new URL(branding.frontendInfoEndpoint);
            return `${url.protocol}//${url.host}/_preview/${locale}?noredirect=1`;
        }
        return '';
    };

    const openInNewTab = (locale: string = i18n) => {
        if (inNewTab) {
            windowRef.current?.close();
            setInNewTab(false);
        } else {
            setWindow(
                window.open(previewUrl ?? buildPreviewUrl(locale), '_blank'),
            );
            setInNewTab(true);
        }
    };

    const setPreviewToken = (previewToken: string | false) => {
        if (previewToken && branding?.frontendInfoEndpoint) {
            const url = new URL(branding.frontendInfoEndpoint);
            setPreviewUrl(
                `${url.protocol}//${url.host}/public-preview/${previewToken}?noredirect=1`,
            );
        } else {
            setPreviewUrl(undefined);
        }
    };

    return useMemo(() => {
        return (
            <PreviewContext.Provider
                value={{
                    previewUrl,
                    showPreview,
                    inNewTab,
                    previewSize,
                    buildPreviewUrl,
                    setPreviewSize,
                    updatePreview,
                    setWindow,
                    openInNewTab,
                    setPreviewToken,
                }}>
                {children}
            </PreviewContext.Provider>
        );
    }, [previewUrl, showPreview, inNewTab, previewSize, children]);
}

export const usePreviewContext = () => {
    return useContext(PreviewContext);
};
